import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  fetchDeletedAssets,
  restoreAsset,
  destroyAsset,
  postRequestSignedUrls
} from '../../../assets/requests'
import { useUserInfo } from '../../Authentication/hooks'
import {
  createNavigationHandlers,
  handleAsyncError
} from './utils/categoryUtils'
import placeholderImg from '../../../assets/images/camera.jpg'
import '../../../assets/stylesheets/gallery-style.css'
import '../../../assets/stylesheets/assets.css'
import '../../../assets/stylesheets/duplicates.css'
import upArrow from '../../../assets/images/up-arrow.png'
import backArrow from '../../../assets/images/back-arrow3.png'
import homeIcon from '../../../assets/images/home-icon.png'
import { toast } from 'react-toastify'

const DeletedCategory = () => {
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshTrigger, setRefreshTrigger] = useState(false)
  const { userId } = useUserInfo()
  const navigate = useNavigate()

  const { handleHomeClick, handleBackClick, handleBackToTop } =
    createNavigationHandlers(navigate)

  const signAssets = useCallback(
    async rawAssets => {
      if (!userId || !Array.isArray(rawAssets) || rawAssets.length === 0) return rawAssets

      // Clone to avoid mutating the input
      const result = rawAssets.map(a => ({ ...a }))

      let thumbnailKeys = []
      let thumbnailIdx = []
      let s3Uris = []
      let s3UriIdx = []

      result.forEach((asset, idx) => {
        if (!asset.thumbnailUrl && asset.thumbnails && asset.thumbnails.length > 0) {
          thumbnailKeys.push(asset.thumbnails[1])
          thumbnailIdx.push(idx)
        }
        if (!asset.fileUrl && asset.s3uri) {
          s3Uris.push(asset.s3uri)
          s3UriIdx.push(idx)
        }
      })

      const [signedThumbnailUrls, signedS3Uris] = await Promise.all([
        thumbnailKeys.length > 0
          ? postRequestSignedUrls({ userId, payload: thumbnailKeys })
          : [],
        s3Uris.length > 0
          ? postRequestSignedUrls({ userId, payload: s3Uris })
          : []
      ])

      thumbnailIdx.forEach((assetIdx, i) => {
        if (signedThumbnailUrls[i]) {
          result[assetIdx].thumbnailUrl = signedThumbnailUrls[i]
        }
      })

      s3UriIdx.forEach((assetIdx, i) => {
        if (signedS3Uris?.[i]) {
          result[assetIdx].fileUrl = signedS3Uris[i]
        }
      })

      return result
    },
    [userId]
  )

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError('')
        const rawAssets = await fetchDeletedAssets({ userId })
        // Normalize to array (backend may return array or object)
        const assetArray = Array.isArray(rawAssets)
          ? rawAssets
          : rawAssets && typeof rawAssets === 'object'
            ? Object.values(rawAssets)
            : []
        if (assetArray.length > 0) {
          const signedAssets = await signAssets(assetArray)
          setAssets(signedAssets)
        } else {
          setAssets([])
        }
      } catch (error) {
        const errorMessage = handleAsyncError(error, 'fetching deleted assets')
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [userId, signAssets, refreshTrigger])

  const handleRestore = useCallback(async (assetId) => {
    try {
      await restoreAsset({ userId, assetId })
      toast.success('Asset restored', { autoClose: 1000, hideProgressBar: true })
      setAssets(prev => prev.filter(a => a.id !== assetId))
    } catch (err) {
      console.error('Failed to restore asset:', err)
      toast.error('Failed to restore asset', { autoClose: 2000, hideProgressBar: true })
    }
  }, [userId])

  const handlePermanentDelete = useCallback(async (assetId) => {
    if (!window.confirm('Permanently delete this asset? This cannot be undone.')) return
    try {
      await destroyAsset({ userId, assetId })
      toast.success('Asset permanently deleted', { autoClose: 1000, hideProgressBar: true })
      setAssets(prev => prev.filter(a => a.id !== assetId))
    } catch (err) {
      console.error('Failed to permanently delete asset:', err)
      toast.error('Failed to permanently delete asset', { autoClose: 2000, hideProgressBar: true })
    }
  }, [userId])

  const handleEmptyTrash = async () => {
    if (assets.length === 0) return
    if (!window.confirm(`Permanently delete ${assets.length} asset(s)? This cannot be undone.`)) return

    const results = await Promise.allSettled(
      assets.map(asset => destroyAsset({ userId, assetId: asset.id }))
    )

    const failCount = results.filter(r => r.status === 'rejected').length
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} asset(s).`, { autoClose: 2000, hideProgressBar: true })
    } else {
      toast.success('Trash emptied', { autoClose: 1000, hideProgressBar: true })
    }
    setRefreshTrigger(prev => !prev)
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>
  }

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <div className="duplicates-category-container">
      <h2 className="row-title-duplicates">Deleted</h2>
      <div className="go-back">
        <img className="nav-icon-home home fav" alt="Go to base of navigation" src={homeIcon} onClick={handleHomeClick} />
        <img className="nav-icon-back back" alt="Go back one level." src={backArrow} onClick={handleBackClick} />
        <img className="nav-icon-top top" alt="Go back to top." src={upArrow} onClick={handleBackToTop} />
      </div>

      <div className="trash-warning-banner">
        Assets in trash will be permanently deleted 30 days after deletion.
      </div>

      {assets.length > 0 && (
        <div className="bulk-action-bar">
          <button className="btn-delete-selected" onClick={handleEmptyTrash}>
            Empty Trash ({assets.length})
          </button>
        </div>
      )}

      {assets.length === 0 ? (
        <div className="gallery-container">
          <div className="empty-state-message">
            <p>No deleted assets.</p>
          </div>
        </div>
      ) : (
        <div className="gallery-container">
          {assets.map(asset => (
            <div key={asset.id} className="gallery-thumb-duplicates">
              <div
                className="gallery-thumb2"
                style={{
                  backgroundImage: `url('${asset.thumbnailUrl || placeholderImg}')`,
                  opacity: 0.7
                }}
              >
                <button
                  className="thumb-restore-overlay"
                  onClick={(e) => { e.stopPropagation(); handleRestore(asset.id) }}
                  title="Restore"
                >
                  ↩
                </button>
                <button
                  className="thumb-action-overlay"
                  onClick={(e) => { e.stopPropagation(); handlePermanentDelete(asset.id) }}
                  title="Delete permanently"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default DeletedCategory
